import React from 'react';
import { XMarkIcon, EyeIcon } from '@heroicons/react/24/outline';
import DetailField from '../ui/DetailField';
import EmailStatusBadge from '../ui/EmailStatusBadge';
import Button from '../ui/Button';

interface EmailLogDetail {
  logId: string;
  status: string;
  reason: string | null;
  /** Present only for emails that were scheduled rather than sent immediately. */
  scheduledFor?: string | null;
  sendAt: string;
  body: string;
  subject: string;
  templateName?: string;
  contact: {
    id: string;
    firstName: string;
    lastName: string;
    email: string;
    company: string;
  };
}

interface EmailDetailsModalProps {
  email: EmailLogDetail | null;
  isResending: boolean;
  onClose: () => void;
  onViewEmail: () => void;
  onResend: () => void;
}

const EmailDetailsModal: React.FC<EmailDetailsModalProps> = ({
  email,
  isResending,
  onClose,
  onViewEmail,
  onResend,
}) => {
  if (!email) return null;

  // Format date to MM/DD/YYYY, HH:MM AM/PM
  const formatDate = (dateString?: string | null): string => {
    if (!dateString) return '-';
    try {
      const date = new Date(dateString);
      if (isNaN(date.getTime())) return '-';
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');
      const year = date.getFullYear();

      let hours = date.getHours();
      const minutes = String(date.getMinutes()).padStart(2, '0');
      const ampm = hours >= 12 ? 'PM' : 'AM';
      hours = hours % 12;
      hours = hours ? hours : 12;

      return `${month}/${day}/${year}, ${hours}:${minutes} ${ampm}`;
    } catch {
      return '-';
    }
  };

  // Decode base64 reason if it's encoded and handle HTML entities
  const decodeReason = (reason: string | null): string => {
    if (!reason) return 'Unknown error occurred while sending the email.';

    try {
      let decoded = reason;

      // Check if it looks like base64 (contains only valid base64 characters)
      const base64Regex = /^[A-Za-z0-9+/=]+$/;
      if (base64Regex.test(reason) && reason.length > 20) {
        decoded = atob(reason);
      }

      // Decode HTML entities
      const textArea = document.createElement('textarea');
      textArea.innerHTML = decoded;
      return textArea.value;
    } catch {
      // If decoding fails, return the original reason
      return reason;
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-md mx-4">
        {/* Modal Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-neutral-100">
          <h3 className="text-lg font-semibold text-black">Email Details</h3>
          <button
            onClick={onClose}
            aria-label="Close"
            className="border border-transparent p-1 transition-colors rounded-md text-neutral-400 hover:text-primary-700 hover:bg-primary-50 focus:outline-none focus-visible:border-primary-700"
          >
            <XMarkIcon className="w-5 h-5" />
          </button>
        </div>

        {/* Modal Body */}
        <dl className="px-6 py-4 space-y-4">
          <DetailField
            label="Recipient"
            value={
              <>
                <span className="block font-medium">
                  {email.contact.firstName} {email.contact.lastName}
                </span>
                <span className="block text-xs text-neutral-500">{email.contact.email}</span>
              </>
            }
          />

          <DetailField label="Subject" value={email.subject} />

          <DetailField label="Template" value={email.templateName || 'Default Template'} />

          <DetailField label="Status" value={<EmailStatusBadge status={email.status} />} />

          {/* Scheduled For - only for emails that were scheduled */}
          {email.scheduledFor && (
            <DetailField label="Scheduled For" value={formatDate(email.scheduledFor)} />
          )}

          <DetailField label="Sent At" value={formatDate(email.sendAt)} />

          {/* Failure Reason - Only show if status is FAILED */}
          {email.status === 'FAILED' && (
            <div className="p-3 border rounded-lg bg-danger-50 border-danger-200">
              <dt className="mb-1 text-xs font-medium tracking-wide uppercase text-danger-800">Failure Reason</dt>
              <dd className="text-sm break-words text-danger-700">
                {decodeReason(email.reason)}
              </dd>
            </div>
          )}
        </dl>

        {/* Modal Footer */}
        <div className="flex items-center justify-between gap-3 px-6 py-4 border-t rounded-b-lg border-neutral-100 bg-neutral-50">
          <Button
            variant="secondary"
            onClick={onViewEmail}
          >
            <EyeIcon className="w-4 h-4" />
            View Email
          </Button>

          <div className="flex items-center gap-3">
            <Button
              variant="neutral"
              onClick={onClose}
            >
              Close
            </Button>

            {/* Resend button - Only show if status is FAILED */}
            {email.status === 'FAILED' && (
              <Button
                onClick={onResend}
                disabled={isResending}
              >
                {isResending ? (
                  <span className="flex items-center gap-2">
                    <span className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></span>
                    Resending...
                  </span>
                ) : (
                  'Resend'
                )}
              </Button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default EmailDetailsModal;